import { useState, useEffect } from 'react';
import { checkFeatureToggle } from '../services/FeatureToggle';

interface UseFeatureToggleResult {
  isEnabled: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * React hook to check if a feature toggle is enabled for the current user's organization
 * @param key - The feature toggle key to check
 * @returns Object containing isEnabled status, loading state, and error
 *
 * @example
 * const { isEnabled, isLoading } = useFeatureToggle('ai-meeting-assistant');
 * if (isEnabled) {
 *   return <MeetingsFeature />;
 * }
 */
export const useFeatureToggle = (key: string): UseFeatureToggleResult => {
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchToggle = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await checkFeatureToggle(key);

        if (response.success && response.data) {
          setIsEnabled(response.data.isEnabled);
        } else {
          // If toggle doesn't exist or API fails, default to disabled
          setIsEnabled(false);
          setError(response.errors?.[0]?.title || 'Failed to check feature toggle');
        }
      } catch (err) {
        setIsEnabled(false);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    if (key) {
      fetchToggle();
    }
  }, [key]);

  return { isEnabled, isLoading, error };
};

